import React, { useEffect, useState } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { getHistoryPayments, saveRootBeforeLogin, getStripeAccount, onChangeLanguage } from './actions'
import { useDispatch } from 'react-redux'
import SimpleSpinner from './components/Common/SimpleSpinner'
import ProtectedRoute from './ProtectedRoute'
import ContactUsTraining from './containers/ContactUs/ContactUsTraining'
import { useCookies } from 'react-cookie'
import ReleaseNotes from './components/ReleaseNotes/ReleaseNotes'
import { expiresDate } from './utils/cookies'
import { IntlNavigate } from './components/Common/IntlComponents'
import { defaultLang, supportedLangs } from './constants'
import useIntlRoute from './customHooks/useIntlRoute'
import { LangType } from './interface'

const GOLEMTransition = React.lazy(() => import('./components/GOLEMTransition'))
const JobOfferWeb = React.lazy(() => import('./components/JobOffers/JobOfferWeb'))
const JobOfferCad = React.lazy(() => import('./components/JobOffers/JobOfferCad'))
const Matrixgold = React.lazy(() => import('./containers/MatrixGold/Matrixgold'))
const EducationRoutes = React.lazy(() => import('./components/Education/EducationRoutes'))
const EndOfSupport = React.lazy(() => import('./containers/EndOfSupport'))
const FAQ = React.lazy(() => import('./containers/FAQ'))
const Legal = React.lazy(() => import('./containers/Legal/Legal'))
const UserArea = React.lazy(() => import('./components/UserArea'))
const CounterSketch = React.lazy(() => import('./containers/CounterSketch'))
const Login = React.lazy(() => import('./containers/Login'))
const OptOut = React.lazy(() => import('./containers/Legal/OptOut'))
const TechSupport = React.lazy(() => import('./containers/TechSupport'))
const Store = React.lazy(() => import('./containers/Store'))
const Error404 = React.lazy(() => import('./containers/Error404'))
const Sales = React.lazy(() => import('./containers/Sales'))
const Checkout = React.lazy(() => import('./containers/Checkout'))
const ContactUsDealers = React.lazy(() => import('./containers/ContactUs/ContactUsDealers'))
const ConfirmationEmailSent = React.lazy(() => import('./containers/ContactUs/ConfirmationEmailSent'))
const News = React.lazy(() => import('./containers/News/News'))
const Accommodations = React.lazy(() => import('./containers/Accommodations/Accommodations'))
const Home = React.lazy(() => import('./containers/Home/Home'))
const TrainingConfirmation = React.lazy(() => import('./containers/TrainingConfirmation'))
const ServerUpdate = React.lazy(() => import('./containers/ServerUpdate'))
const DesignPersonaQuiz = React.lazy(() => import('./containers/DesignPersonaQuiz'))

const getRouteTree = (selectedProduct: string) => [
  { path: '', element: <Home /> },
  { path: 'matrixgold/*', element: <Matrixgold /> },
  { path: 'countersketch/*', element: <CounterSketch /> },
  { path: 'license-system-update', element: <GOLEMTransition /> },
  { path: 'job/044', element: <JobOfferWeb /> },
  { path: 'job/043', element: <JobOfferCad /> },
  { path: 'education/*', element: <EducationRoutes /> },
  { path: 'end-of-support', element: <EndOfSupport /> },
  { path: 'faq', element: <FAQ /> },
  { path: 'legal/*', element: <Legal /> },
  { path: 'server-update', element: <ServerUpdate /> },
  {
    path: 'user-area/*',
    element: (
      <ProtectedRoute>
        <UserArea />
      </ProtectedRoute>
    )
  },
  { path: 'login', element: <Login /> },
  { path: 'opt-out', element: <OptOut /> },
  { path: 'tech-support', element: <TechSupport /> },
  { path: 'store/*', element: <Store /> },
  { path: 'micro-frontend-store', element: <IntlNavigate to='/store' /> },
  { path: 'sales', element: <Sales /> },
  {
    path: 'checkout/*',
    element: (
      <ProtectedRoute>
        <Checkout />
      </ProtectedRoute>
    )
  },
  { path: 'sendEmail', element: <ContactUsDealers selectedProduct={selectedProduct} /> },
  {
    path: 'private-classes',
    element: (
      <ProtectedRoute>
        <ContactUsTraining selectedProduct={selectedProduct} />
      </ProtectedRoute>
    )
  },
  {
    path: 'private-classes/confirmation',
    element: (
      <ProtectedRoute>
        <TrainingConfirmation />
      </ProtectedRoute>
    )
  },
  { path: 'release-candidate-notes', element: <ReleaseNotes software='release-notes' activeSlug={false} /> },
  { path: 'release-candidate-notes/:slug', element: <ReleaseNotes software='release-notes' activeSlug={true} /> },
  { path: 'news', element: <News /> },
  { path: 'news/:slug', element: <News activeSlug={true} /> },
  { path: 'accommodations', element: <Accommodations /> },
  { path: 'confirmation-email-sent', element: <ConfirmationEmailSent /> },
  { path: 'design-persona-quiz', element: <DesignPersonaQuiz /> },
  { path: '*', element: <Error404 /> }
]

const GemvisionRoutes = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const intlRoute = useIntlRoute()
  const { pathname } = useLocation()
  const [selectedProduct, setSelectedProduct] = useState('')
  const [cookies, setCookies] = useCookies()
  const currentRouteLang = pathname.split('/')[1] as LangType

  useEffect(() => {
    // Force English if /en is visited
    if (currentRouteLang === 'en') {
      dispatch(onChangeLanguage(defaultLang))
      setCookies('language', defaultLang, { path: '/', expires: expiresDate })
      return
    }
    if (cookies.language !== undefined) {
      // If the route is a supported language (not 'en') and doesn't match the cookie, sync to the route
      if (
        supportedLangs.includes(currentRouteLang) &&
        currentRouteLang !== defaultLang &&
        cookies.language !== currentRouteLang
      ) {
        dispatch(onChangeLanguage(currentRouteLang))
        setCookies('language', currentRouteLang, { path: '/', expires: expiresDate })
      }
      // If the route is not a supported language (i.e., English, no prefix), sync to default language
      else if (
        (!supportedLangs.includes(currentRouteLang) && cookies.language !== defaultLang) ||
        (!supportedLangs.includes(currentRouteLang) && !cookies.functionalityCookies)
      ) {
        dispatch(onChangeLanguage(defaultLang))
        setCookies('language', defaultLang, { path: '/', expires: expiresDate })
      }
    }
  }, [pathname])

  const changeRoute = (event: any) => {
    console.log('changeRoute', event.detail)
    navigate(intlRoute(event.detail))
  }

  const getProductContactUs = (event: any) => {
    setSelectedProduct(event.detail.product)
    const copy = { ...event, detail: event.detail.url }
    changeRoute(copy)
  }

  const successPurchase = (event: any) => {
    dispatch(getStripeAccount(event.detail.email))
    dispatch(getHistoryPayments(event.detail.customerId))
  }

  useEffect(() => {
    if (!pathname.includes('/login') && !pathname.includes('/create-account')) {
      dispatch(saveRootBeforeLogin(pathname))
    }
    window.scrollTo(0, 0)
  }, [pathname, dispatch])

  useEffect(() => {
    window.addEventListener('changeRoute', changeRoute)
    window.addEventListener('sendProductContactUs', getProductContactUs)
    window.addEventListener('successPurchase', successPurchase)
    return () => {
      window.removeEventListener('changeRoute', changeRoute)
      window.removeEventListener('sendProductContactUs', getProductContactUs)
      window.removeEventListener('successPurchase', successPurchase)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const langPrefixes = ['', ...supportedLangs.filter((lang) => lang !== defaultLang)]

  return (
    <React.Suspense fallback={<SimpleSpinner />}>
      <Routes>
        {langPrefixes.map((langPrefix) => (
          <Route path={`${langPrefix && '/' + langPrefix}/*`} key={langPrefix}>
            {getRouteTree(selectedProduct).map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Route>
        ))}
      </Routes>
    </React.Suspense>
  )
}

export default GemvisionRoutes
